import PropTypes from 'prop-types';
import { Fragment, memo, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Tag from 'shopper/components/Tag/V1';
import Text from 'shopper/components/Text';

import OFFER from 'constants/offer';

const MAX_CHARACTER_LENGTH_ON_OFFER_TAGS_COMBINED = 27;

const OfferCardTags = ({
  className,
  limitOffersTagsByOfferCardSize = true,
  offerTags = [],
  offerUserVisibility,
  ...rest
}) => {
  const tags = useMemo(() => {
    const _tags = [...offerTags];
    const isOfferExclusiveForApp =
      offerUserVisibility === OFFER.VISIBILITY_STATUS.APP_ONLY;

    if (isOfferExclusiveForApp) {
      _tags.unshift({ name: OFFER.BADGES.APP_ONLY });
    }

    if (!limitOffersTagsByOfferCardSize) {
      return _tags;
    }

    return _tags.reduce((acc, { name }, index) => {
      const namesStringLength = acc.reduce((a, b) => a + b.length, 0);

      if (
        index === 0 ||
        namesStringLength + name.length <
          MAX_CHARACTER_LENGTH_ON_OFFER_TAGS_COMBINED
      ) {
        acc.push(name);
      }

      return acc;
    }, []);
  }, [offerTags, limitOffersTagsByOfferCardSize, offerUserVisibility]);

  const tagNameTotalStringLength = useMemo(
    () => tags.reduce((a, b) => a + b.length, 0),
    [tags]
  );

  if (tags.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        'flex flex-nowrap content-start overflow-x-hidden',
        className
      )}
      {...rest}
    >
      {tags.map((tagName) => (
        <Fragment key={tagName}>
          <Tag
            className="mr-1 whitespace-pre px-2 py-1 last:mr-0 md:mb-2"
            type={tagName === OFFER.BADGES.APP_ONLY ? 'secondary' : 'neutral'}
            filled
          >
            {tagName}
          </Tag>
        </Fragment>
      ))}
      {tagNameTotalStringLength !==
        MAX_CHARACTER_LENGTH_ON_OFFER_TAGS_COMBINED &&
        offerTags.length !== tags.length && (
          <Text as="span" className="ml-1" size="size2">
            ...
          </Text>
        )}
    </div>
  );
};

OfferCardTags.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  limitOffersTagsByOfferCardSize: PropTypes.bool,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerUserVisibility: PropTypes.string.isRequired,
};

export default memo(OfferCardTags);
